/**
 * DesignONUNode - Editable ONU node for network design with customer count
 */
import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';

export interface DesignONUNodeData {
  label: string;
  expectedCustomers: number;
  inputPower?: number; // Calculated from upstream
}

export const DesignONUNode: React.FC<NodeProps> = ({ id, data: nodeData, selected }) => {
  const data = nodeData as unknown as DesignONUNodeData;
  const { updateNodeData, deleteElements } = useReactFlow();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(data.label);

  const handleLabelChange = () => {
    updateNodeData(id as string, {
      ...data,
      label: labelValue,
    });
    setIsEditingLabel(false);
  };

  const handleCustomersChange = (value: number) => {
    updateNodeData(id as string, {
      ...data,
      expectedCustomers: value,
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete ${data.label}?`)) {
      deleteElements({ nodes: [{ id: id as string }] });
    }
  };

  // Signal quality indicator
  const getSignalQuality = (power?: number) => {
    if (!power) return { color: '#999', label: 'Unknown', icon: '⚫' };
    if (power >= -8) return { color: '#4caf50', label: 'Excellent', icon: '🟢' };
    if (power >= -15) return { color: '#ffeb3b', label: 'Good', icon: '🟡' };
    if (power >= -25) return { color: '#ff9800', label: 'Fair', icon: '🟠' };
    if (power >= -28) return { color: '#f44336', label: 'Weak', icon: '🔴' };
    return { color: '#9e9e9e', label: 'Critical', icon: '⛔' };
  };

  const signal = getSignalQuality(data.inputPower);

  return (
    <div className={`design-node design-onu-node ${selected ? 'selected' : ''}`}>
      <div className="design-node-header">
        <span>📡</span>
        {isEditingLabel ? (
          <input
            type="text"
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onBlur={handleLabelChange}
            onKeyDown={(e) => {
              e.stopPropagation(); // Prevent node deletion when typing
              if (e.key === 'Enter') handleLabelChange();
              if (e.key === 'Escape') {
                setLabelValue(data.label);
                setIsEditingLabel(false);
              }
            }}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              outline: 'none',
              width: '120px',
            }}
          />
        ) : (
          <span
            onDoubleClick={() => setIsEditingLabel(true)}
            style={{ cursor: 'text' }}
            title="Dvojklik na úpravu"
          >
            {data.label}
          </span>
        )}
        <button
          onClick={handleDelete}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '2px 4px',
            marginLeft: 'auto',
          }}
          title="Zmazať ONU"
        >
          🗑️
        </button>
      </div>
      <div className="design-node-body">
        <div className="design-field">
          <span className="design-field-label">Customers:</span>
          <input
            type="number"
            className="design-field-input"
            value={data.expectedCustomers}
            min="1"
            onChange={(e) => handleCustomersChange(parseInt(e.target.value) || 1)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        {data.inputPower !== undefined && (
          <>
            <div className="design-field">
              <span className="design-field-label">Signal:</span>
              <span className="design-field-value" style={{ color: signal.color }}>
                {signal.icon} {data.inputPower.toFixed(2)} dBm
              </span>
            </div>
            <div className="design-field">
              <span className="design-field-label">Quality:</span>
              <span className="design-field-value" style={{ color: signal.color }}>
                {signal.label}
              </span>
            </div>
          </>
        )}
        {data.inputPower !== undefined && data.inputPower < -28 && (
          <div className="design-error">
            ⚠️ Signal too weak! Add splitter or reduce distance.
          </div>
        )}
        {data.expectedCustomers > 1 && (
          <div className="design-warning">
            ⚠️ {data.expectedCustomers} customers - consider adding splitter
          </div>
        )}
      </div>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ background: '#4caf50' }}
      />
    </div>
  );
};

